import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Users, X } from 'lucide-react';
import type { AdminUser } from '../../generated/api/types.gen.js';
import { Avatar, Badge, Button, DataTable, EmptyState } from '../../components/ui/index.js';
import type { DataTableColumn } from '../../components/ui/index.js';
import { Checkbox } from '../../components/tailgrids/core/checkbox.js';
import { Can } from '../../components/auth/Can.js';
import { PAGE_HELP } from '../../help/index.js';
import {
  USER_STATUS_LABEL,
  USER_STATUS_VARIANT,
  formatDate,
  roleBadgeVariant,
  roleLabel,
  userDisplayName,
} from './user-labels.js';

interface UsersTableProps {
  users: AdminUser[];
  loading: boolean;
  selectedIds: Set<number>;
  onToggleSelect: (id: number) => void;
  onSelectAll: () => void;
  onRowClick: (user: AdminUser) => void;
  onDecide: (id: number, status: 'approved' | 'rejected') => void;
  deciding: boolean;
  /** Shown in the empty state when no filter is active. */
  emptyAction?: React.ReactNode;
}

/** The users list body: selectable rows, name linking to the detail page, inline approve / reject for pending sign-ups. */
export const UsersTable: React.FC<UsersTableProps> = ({
  users,
  loading,
  selectedIds,
  onToggleSelect,
  onSelectAll,
  onRowClick,
  onDecide,
  deciding,
  emptyAction,
}) => {
  const help = PAGE_HELP.users;
  const allSelected = users.length > 0 && users.every((u) => selectedIds.has(u.id));
  const someSelected = users.some((u) => selectedIds.has(u.id)) && !allSelected;

  const columns: DataTableColumn<AdminUser>[] = [
    {
      key: 'select',
      width: '44px',
      header: (
        <Can policies={['CanManageUsers', 'CanManageOrgMembers']} mode="any">
          <Checkbox
            aria-label="Select all users on this page"
            isSelected={allSelected}
            isIndeterminate={someSelected}
            onChange={onSelectAll}
          />
        </Can>
      ),
      render: (user) => (
        <Can policies={['CanManageUsers', 'CanManageOrgMembers']} mode="any">
          <Checkbox
            aria-label={`Select ${userDisplayName(user)}`}
            isSelected={selectedIds.has(user.id)}
            onChange={() => onToggleSelect(user.id)}
          />
        </Can>
      ),
    },
    {
      key: 'user',
      header: 'User',
      sortable: true,
      sortValue: (user) => userDisplayName(user).toLowerCase(),
      render: (user) => (
        <div className="flex min-w-0 items-center gap-3">
          <Avatar kind="human" label={userDisplayName(user)} size="sm" />
          <div className="min-w-0">
            <Link
              to={`/organization/users/${user.id}`}
              className="block truncate text-sm font-medium text-heading hover:text-accent-text"
              onClick={(e) => e.stopPropagation()}
            >
              {userDisplayName(user)}
            </Link>
            <span className="block truncate text-xs text-muted">{user.email}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'roles',
      header: 'Roles',
      render: (user) =>
        user.roles && user.roles.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {user.roles.map((role) => (
              <Badge key={role.id} variant={roleBadgeVariant(role.roleName)}>
                {roleLabel(role.roleName)}
              </Badge>
            ))}
          </div>
        ) : (
          <span className="text-sm text-faint">No roles</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      sortValue: (user) => user.status,
      render: (user) => (
        <Badge variant={user.active ? USER_STATUS_VARIANT[user.status] : 'neutral'} dot>
          {user.active ? USER_STATUS_LABEL[user.status] : 'Deactivated'}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      header: 'Registered',
      sortable: true,
      sortValue: (user) => user.createdAt,
      render: (user) => <span className="text-sm text-muted">{formatDate(user.createdAt)}</span>,
    },
    {
      key: 'lastLoginAt',
      header: 'Last sign-in',
      sortable: true,
      sortValue: (user) => user.lastLoginAt,
      render: (user) => <span className="text-sm text-muted">{formatDate(user.lastLoginAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '190px',
      render: (user) =>
        user.status === 'pending' ? (
          <Can policies={['CanManageUsers', 'CanManageOrgMembers']} mode="any">
            <div className="flex items-center justify-end gap-1.5">
              <Button size="sm" variant="success" disabled={deciding} onClick={() => onDecide(user.id, 'approved')}>
                <Check size={14} aria-hidden="true" />
                Approve
              </Button>
              <Button size="sm" variant="secondary" disabled={deciding} onClick={() => onDecide(user.id, 'rejected')}>
                <X size={14} aria-hidden="true" />
                Reject
              </Button>
            </div>
          </Can>
        ) : null,
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={users}
      rowKey={(user) => user.id}
      getRowAttention={(user) => (user.status === 'pending' ? 'warning' : undefined)}
      onRowClick={onRowClick}
      loading={loading}
      aria-label="Users"
      empty={
        <EmptyState
          icon={Users}
          title={help.emptyState.title}
          description={help.emptyState.description}
          learnItems={help.emptyState.learnItems}
          action={emptyAction}
        />
      }
    />
  );
};
